import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  DataGridPro,
  DataGridProProps,
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { useResizableColumns } from "../../hooks/useResizableColumns";
import { usePersistedColumnVisibility } from "../../hooks/usePersistedColumnVisibility";
import CustomLoadingOverlay from "../../components/ui/CustomLoadingOverlay";
import { dataGridHeaderHeight, GRIDCOLUMNKEYS } from "../../data/constants";
import { EDITABLE_LIST_STATUSES, PifListItem } from "../../pages/supervision/PostInterviewForm/types";
import PostIFStatusChip from "./PostIFStatusChip";
import PostIFApprovalDots from "./PostIFApprovalDots";

export type PostIFTableVariant = "my" | "hr";

// Title and Dept · Position stack two lines per cell, so the shared 45px row is too short:
// the second line clips. Keep this PIF-specific height rather than dataGridRowHeight.
const POSTIF_ROW_HEIGHT = 56;

// Rows revealed per scroll-end, the same load-as-you-scroll grid the Manage Users / Activities
// lists use. Client-side here, unlike those: /postif/my-list takes no pageNumber/pageSize and
// returns the caller's whole scope in one array (hr-list is asked for a single 200-row page), so
// there is no next page to fetch - the chunking exists to keep the DOM small and to stop the card
// growing without limit. If my-list ever gains paging, only the source of `rows` changes; the
// scroll handling below stays as it is.
const POSTIF_SCROLL_CHUNK = 25;

// MUI injects Button's base styles (min-width 64px, size padding, centred content) through
// emotion, which outranks a plain BEM class — these have to ride on sx to take effect.
const TITLE_BUTTON_SX = {
  minWidth: 0,
  padding: 0,
  fontSize: "13px",
  fontWeight: 400,
  lineHeight: 1.35,
  textAlign: "left",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  height: "100%",
  width: "100%",
  gap: "3px",
  color: "inherit",
  "&:hover": { background: "transparent" },
} as const;

interface PostIFTableProps {
  variant: PostIFTableVariant;
  rows: PifListItem[];
  loading?: boolean;
  onEdit: (row: PifListItem) => void;
  onOpenDetail: (row: PifListItem) => void;
  onOpen: (row: PifListItem) => void;
  onDelete: (row: PifListItem) => void;
  onMarkAsEmployee: (row: PifListItem) => void;
}

const PostIFTable: React.FC<PostIFTableProps> = ({
  variant,
  rows,
  loading = false,
  onEdit,
  onOpenDetail,
  onOpen,
  onDelete,
  onMarkAsEmployee,
}) => {
  const isHrView = variant === "hr";

  const columns = useMemo<GridColDef<PifListItem>[]>(() => {
    const titleColumn: GridColDef<PifListItem> = {
      field: "title",
      headerName: "Title",
      flex: 1.4,
      minWidth: 170,
      renderCell: (params: GridRenderCellParams<PifListItem>) => (
        <Button
          type="button"
          variant="text"
          sx={TITLE_BUTTON_SX}
          onClick={() => onOpenDetail(params.row)}
        >
          <span className="postif-grid__title-main">{params.row.title}</span>
          <span className="postif-grid__title-sub">{params.row.flow}</span>
        </Button>
      ),
    };

    const applicantColumn: GridColDef<PifListItem> = {
      field: "applicant",
      headerName: "Applicant",
      flex: 0.9,
      minWidth: 110,
    };

    const createdByColumn: GridColDef<PifListItem> = {
      field: "createdBy",
      headerName: "Created by",
      flex: 0.8,
      minWidth: 100,
    };

    const deptPositionColumn: GridColDef<PifListItem> = {
      field: "department",
      headerName: "Dept · Position",
      flex: 1.2,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<PifListItem>) => (
        <span className="postif-grid__stack">
          <span>{params.row.department}</span>
          <span className="postif-grid__stack-sub">{params.row.position}</span>
        </span>
      ),
    };

    const typeColumn: GridColDef<PifListItem> = {
      field: "employmentType",
      headerName: "Type",
      flex: 0.4,
      minWidth: 62,
    };

    const approvalColumn: GridColDef<PifListItem> = {
      field: "approvals",
      headerName: "Approval",
      flex: 0.6,
      minWidth: 96,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<PifListItem>) => (
        <PostIFApprovalDots steps={params.row.approvals} />
      ),
    };

    const statusColumn: GridColDef<PifListItem> = {
      field: "status",
      headerName: "Status",
      flex: 0.9,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams<PifListItem>) => (
        <PostIFStatusChip status={params.row.status} />
      ),
    };

    const updatedColumn: GridColDef<PifListItem> = {
      field: "updated",
      headerName: "Updated",
      flex: 0.7,
      minWidth: 104,
    };

    const employeeNumberColumn: GridColDef<PifListItem> = {
      field: "employeeNumber",
      headerName: "Employee #",
      flex: 0.7,
      minWidth: 108,
      renderCell: (params: GridRenderCellParams<PifListItem>) =>
        params.row.employeeNumber ? (
          <span className="postif-grid__employee-no">{params.row.employeeNumber}</span>
        ) : (
          <span className="postif-grid__muted">—</span>
        ),
    };

    const actionsColumn: GridColDef<PifListItem> = {
      field: "actions",
      headerName: "Actions",
      width: isHrView ? 140 : 132,
      hideable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: false,
      renderCell: (params: GridRenderCellParams<PifListItem>) => {
        // Only the creator may edit/delete, and only while it's a draft. The supervisor's "my"
        // list also surfaces their reports' forms, so a draft that isn't the caller's own stays
        // read-only. The server re-enforces this (soft-delete is creator-only), so this is UX only.
        const canMutateDraft =
          !isHrView && params.row.isMine && EDITABLE_LIST_STATUSES.includes(params.row.status);
        // 79822 AC5 / Epic 72585: HR converts an APPROVED PostIF (fully signed off) into an employee
        // record — conversion is the post-approval applicant->employee step. The API gates on
        // 'approved' (sProc_Portal_PostIF_MarkAsEmployee_U), so only an Approved, not-yet-converted
        // row is markable; offering it on submitted/returned/declined rows was a dead click that
        // returned WRONG_STATE.
        // Gated on markedAsEmployee, NOT on employeeNumber: an existing employee's number is
        // recorded from draft save now, so `!employeeNumber` would hide this action on precisely
        // the approved existing-employee rows that still need converting.
        const canMarkAsEmployee =
          isHrView &&
          params.row.status === "APPROVED" &&
          !params.row.markedAsEmployee;

        return (
          <Box className="postif-grid__actions">
            {!isHrView && (
              <GridActionsCellItem
                icon={
                  <Tooltip title="Edit" placement="top">
                    <EditIcon fontSize="small" />
                  </Tooltip>
                }
                label="Edit"
                disabled={!canMutateDraft}
                onClick={() => onEdit(params.row)}
              />
            )}
            <GridActionsCellItem
              icon={
                <Tooltip title="View" placement="top">
                  <VisibilityIcon fontSize="small" />
                </Tooltip>
              }
              label="View"
              onClick={() => onOpenDetail(params.row)}
            />
            {isHrView && (
              <GridActionsCellItem
                icon={
                  <Tooltip title="Open" placement="top">
                    <OpenInNewIcon fontSize="small" />
                  </Tooltip>
                }
                label="Open"
                onClick={() => onOpen(params.row)}
              />
            )}
            {isHrView && (
              <GridActionsCellItem
                icon={
                  <Tooltip title="Mark as employee" placement="top">
                    <PersonAddIcon fontSize="small" />
                  </Tooltip>
                }
                label="Mark as employee"
                disabled={!canMarkAsEmployee}
                onClick={() => onMarkAsEmployee(params.row)}
                data-testid="postif-mark-employee"
              />
            )}
            {!isHrView && (
              <GridActionsCellItem
                icon={
                  <Tooltip title="Delete" placement="top">
                    <DeleteIcon fontSize="small" />
                  </Tooltip>
                }
                label="Delete"
                disabled={!canMutateDraft}
                onClick={() => onDelete(params.row)}
              />
            )}
          </Box>
        );
      },
    };

    return isHrView
      ? [
          titleColumn,
          applicantColumn,
          createdByColumn,
          deptPositionColumn,
          approvalColumn,
          statusColumn,
          employeeNumberColumn,
          actionsColumn,
        ]
      : [
          titleColumn,
          applicantColumn,
          deptPositionColumn,
          typeColumn,
          approvalColumn,
          statusColumn,
          updatedColumn,
          actionsColumn,
        ];
  }, [isHrView, onEdit, onOpenDetail, onOpen, onDelete, onMarkAsEmployee]);

  const { columnState, pinnedColumns, setPinnedColumns, handleColumnResize } =
    useResizableColumns(columns);

  // The my/hr variants are different column sets, so each keeps its own saved selection.
  const { columnVisibilityModel, setColumnVisibilityModel } = usePersistedColumnVisibility(
    `${GRIDCOLUMNKEYS.POST_INTERVIEW_FORMS}-${variant}`
  );

  const [visibleCount, setVisibleCount] = useState(POSTIF_SCROLL_CHUNK);

  // Any new row set - a filter, the search box, or a refetch after delete/mark-as-employee -
  // starts again at the first chunk, scrolled back to the top of a shorter list.
  useEffect(() => {
    setVisibleCount(POSTIF_SCROLL_CHUNK);
  }, [rows]);

  const scrolledRows = useMemo(() => rows.slice(0, visibleCount), [rows, visibleCount]);

  const handleRowsScrollEnd: DataGridProProps["onRowsScrollEnd"] = () => {
    setVisibleCount((current) => (current >= rows.length ? current : current + POSTIF_SCROLL_CHUNK));
  };

  return (
    <div className="postif-grid">
      <DataGridPro
        rows={scrolledRows}
        columns={columnState}
        getRowId={(row) => row.id}
        getRowClassName={(params) => `postif-row-${params.row.id}`}
        disableRowSelectionOnClick
        rowHeight={POSTIF_ROW_HEIGHT}
        columnHeaderHeight={dataGridHeaderHeight}
        loading={loading}
        slots={{ loadingOverlay: CustomLoadingOverlay }}
        hideFooter
        // No autoHeight: it renders every row and removes the grid's own scroller, so
        // onRowsScrollEnd would never fire. The height comes from .postif-grid instead.
        onRowsScrollEnd={handleRowsScrollEnd}
        // Actions stays pinned right, matching Manage Users / Activities / Audit Log: the
        // hook defaults to { right: ["actions"] } and the user can unpin from the column menu.
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={(newPinned) => setPinnedColumns(newPinned)}
        onColumnWidthChange={(params) => handleColumnResize(params.width, params.colDef)}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        localeText={{ noRowsLabel: "No Post Interview Forms match the current filters." }}
      />
    </div>
  );
};

export default PostIFTable;
